import React from 'react'
import { Box, Loading, Message, Text, useAppTheme } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { useShiftDetailContextValue } from '../../../context/ShiftDetailProvider'
import { useTierEligibilitiesByShiftIdQuery } from 'queries/shifts/useTierEligibilitiesByShiftIdQuery'
import { FlexibleInfoTable } from '../../FlexibleInfoTable'
import { AlertTriangle } from 'lucide-react'
import { SectionHeading } from '../components/SectionHeading'

type ListingsOverviewContextState = {
  state: {
    listingId?: number
  }
}

const [Context, useListingsOverviewContextValue] =
  createContext<ListingsOverviewContextState>({
    name: 'ListingsOverviewContext',
  })

export const ListingsOverviewProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { colors } = useAppTheme()
  const {
    state: { listings, id: shiftId },
  } = useShiftDetailContextValue()
  /*
  Most of the time, a shift can only have one listing. However, there are cases when a shift have none, or multiple listings.

  If shift has no listings, we show an error message.
  If shift has multiple listings, we use the listingId from the tier stats API, which uses `primary listing` concept in the backend as the main listing.
  */

  const shiftListings = listings ?? []
  const hasNoListings = shiftListings.length === 0
  const hasMultipleListings = shiftListings.length > 1

  const { data: tierStatsData, isLoading: isTierStatsLoading } =
    useTierEligibilitiesByShiftIdQuery(shiftId)

  const listingId = hasMultipleListings
    ? tierStatsData?.listingId
    : shiftListings[0]?.id

  const value = {
    state: {
      listingId,
    },
  }

  if (hasNoListings) {
    return (
      <Context.Provider value={value}>
        <Box>
          <SectionHeading />
          <Message variant="error" borderRadius="small">
            This shift does not have any listings. Listing overview is
            unavailable.
          </Message>
        </Box>
      </Context.Provider>
    )
  }

  if (hasMultipleListings && isTierStatsLoading && !listingId) {
    return <Loading />
  }

  return (
    <Context.Provider value={value}>
      {hasMultipleListings ? (
        <Box mb={3}>
          <FlexibleInfoTable
            Icon={AlertTriangle}
            iconColor={colors.warnings[100]}
          >
            <Text fontSize={2} maxWidth="100%">
              This shift has {shiftListings.length} listings (
              {shiftListings.map((listing) => listing.id).join(', ')}).
            </Text>
            <Text fontSize={2} maxWidth="100%">
              Overview data is shown for listing {listingId}.
            </Text>
          </FlexibleInfoTable>
        </Box>
      ) : null}
      {children}
    </Context.Provider>
  )
}

export { useListingsOverviewContextValue }
